import { useMemo } from "react";
import { getBillingManagementDetails } from "../../../../common/services/billing-management";
import {
  IBillingManagement,
  IBillingManagementDetail,
} from "../../../../types/billing-management.type";
import { useQuery } from "@tanstack/react-query";

interface UseManagementBillingReturn {
  details: IBillingManagementDetail | null;
  isLoading: boolean;
  error: string | null;
  paymentSplitWalletId: string;
  subscriptionId: string;
  percentualValue: number;
}

export const useManagementBilling = (
  payment: IBillingManagement | undefined
): UseManagementBillingReturn => {
  const {
    data: queryResult,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["billing-management-details", payment?.paymentId],
    queryFn: async () => {
      if (!payment?.paymentId) {
        return null;
      }

      const response = await getBillingManagementDetails(payment.paymentId);

      if (!response.ok) {
        throw new Error(response.message || "Erro ao buscar detalhes");
      }

      return response.data;
    },
    enabled: Boolean(payment?.paymentId),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const details: IBillingManagementDetail | null = queryResult || null;
  const errorMessage = error instanceof Error ? error.message : null;

  const latestHistory = useMemo(() => {
    const paymentDetails = details?.payment;
    if (!paymentDetails?.history || paymentDetails.history.length === 0) {
      return null;
    }

    return paymentDetails.history[paymentDetails.history.length - 1]?.data ?? null;
  }, [details?.payment]);

  const paymentSplitWalletId = useMemo(() => {
    if (details?.split?.walletId) {
      return details.split.walletId;
    }

    return Array.isArray(latestHistory?.split) && latestHistory.split.length > 0
      ? latestHistory.split[latestHistory.split.length - 1]?.walletId ?? ""
      : "";
  }, [details?.split, latestHistory]);

  const subscriptionId = useMemo(() => {
    return latestHistory?.subscription ?? "";
  }, [latestHistory]);

  const percentualValue = useMemo(() => {
    if (typeof details?.split?.percentualValue === "number") {
      return details.split.percentualValue;
    }

    return Array.isArray(latestHistory?.split) && latestHistory.split.length > 0
      ? latestHistory.split[latestHistory.split.length - 1]?.percentualValue ?? 0
      : 0;
  }, [details?.split, latestHistory]);

  return {
    details,
    isLoading,
    error: errorMessage,
    paymentSplitWalletId,
    subscriptionId,
    percentualValue,
  };
};
